import { useState } from "react";
import {
  bootstrapRssFeeds,
  createRssFeed,
  deleteRssFeed,
  getRssFeeds,
  updateRssFeed,
  useQuery,
} from "wasp/client/operations";
import { Button, SurfaceCard } from "../../../components/ui";
import FeedEditorDialog from "./FeedEditorDialog";
import LoadingSpinner from "../../layout/LoadingSpinner";

const FeedsTable = () => {
  const { data: feeds, isLoading, error, refetch } = useQuery(getRssFeeds);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [editingFeed, setEditingFeed] = useState<any | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [bootstrapFeedback, setBootstrapFeedback] = useState<{
    tone: "success" | "danger";
    message: string;
  } | null>(null);

  const handleBootstrapFeeds = async () => {
    setIsBootstrapping(true);
    setBootstrapFeedback(null);

    try {
      const { upsertedCount, totalCount } = await bootstrapRssFeeds();
      await refetch();

      if (upsertedCount > 0) {
        setBootstrapFeedback({
          tone: "success",
          message: `Bootstrap complete. Added ${upsertedCount} missing feed${
            upsertedCount === 1 ? "" : "s"
          } (total ${totalCount}).`,
        });
      } else {
        setBootstrapFeedback({
          tone: "success",
          message: `Bootstrap complete. No missing feeds were added (total ${totalCount}).`,
        });
      }
    } catch (bootstrapError) {
      const message =
        bootstrapError instanceof Error
          ? bootstrapError.message
          : "Unknown error";
      setBootstrapFeedback({
        tone: "danger",
        message: `Bootstrap failed: ${message}`,
      });
    } finally {
      setIsBootstrapping(false);
    }
  };

  const getStatusBadge = (feed: any) => {
    if (!feed.active) {
      return (
        <span className="inline-flex items-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
          Inactive
        </span>
      );
    }
    if (feed.errorCount > 0) {
      return (
        <span className="inline-flex items-center rounded-full bg-danger-100 px-2 py-0.5 text-xs font-medium text-danger-600 dark:bg-danger-900/30 dark:text-danger-400">
          Error ({feed.errorCount})
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-success-100 px-2 py-0.5 text-xs font-medium text-success-600 dark:bg-success-900/30 dark:text-success-400">
        Active
      </span>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <SurfaceCard>
        <p className="text-danger-600">
          Error loading RSS feeds: {error.message}
        </p>
      </SurfaceCard>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header actions */}
      <div className="flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          tone="neutral"
          size="sm"
          className="rounded-sm"
          onClick={() => setIsCreateOpen(true)}
        >
          New Feed
        </Button>
        <Button
          onClick={handleBootstrapFeeds}
          loading={isBootstrapping}
          variant="solid"
          tone="primary"
          size="sm"
          className="rounded-sm"
        >
          {isBootstrapping ? "Bootstrapping..." : "Bootstrap Feeds"}
        </Button>
      </div>

      {bootstrapFeedback && (
        <SurfaceCard
          padding="sm"
          className={
            bootstrapFeedback.tone === "success"
              ? "border-success bg-success-100/60 dark:bg-success-900/20"
              : "border-danger bg-danger-100/60 dark:bg-danger-900/20"
          }
        >
          <p
            className={
              bootstrapFeedback.tone === "success"
                ? "text-success-700 dark:text-success-400"
                : "text-danger-700 dark:text-danger-400"
            }
          >
            {bootstrapFeedback.message}
          </p>
        </SurfaceCard>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <SurfaceCard padding="sm">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Total
          </p>
          <p className="mt-1 text-2xl font-semibold text-black dark:text-white">
            {feeds?.length || 0}
          </p>
        </SurfaceCard>
        <SurfaceCard padding="sm">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Active
          </p>
          <p className="mt-1 text-2xl font-semibold text-success-600">
            {feeds?.filter((f) => f.active).length || 0}
          </p>
        </SurfaceCard>
        <SurfaceCard padding="sm">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Inactive
          </p>
          <p className="mt-1 text-2xl font-semibold text-neutral-600">
            {feeds?.filter((f) => !f.active).length || 0}
          </p>
        </SurfaceCard>
        <SurfaceCard padding="sm">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Providers
          </p>
          <p className="mt-1 text-2xl font-semibold text-info">
            {feeds ? new Set(feeds.map((f) => f.provider)).size : 0}
          </p>
        </SurfaceCard>
      </div>

      {/* Feeds Table */}
      <SurfaceCard padding="none">
        {/* Table Header */}
        <div className="grid grid-cols-12 border-b border-stroke py-3 px-4 dark:border-strokedark md:px-6">
          <div className="col-span-2 flex items-center">
            <p className="font-medium">Provider</p>
          </div>
          <div className="col-span-2 flex items-center">
            <p className="font-medium">Name</p>
          </div>
          <div className="col-span-4 flex items-center">
            <p className="font-medium">URL</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Status</p>
          </div>
          <div className="col-span-2 flex items-center">
            <p className="font-medium">Categories</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Actions</p>
          </div>
        </div>

        {/* Feeds List */}
        {feeds && feeds.length > 0 ? (
          feeds.map((feed: any) => (
            <div
              key={feed.id}
              className="grid grid-cols-12 gap-2 border-t border-stroke py-2.5 px-4 dark:border-strokedark md:px-6"
            >
              <div className="col-span-2 flex items-center">
                <div className="flex flex-col gap-1">
                  <p className="text-sm text-black dark:text-white font-medium">
                    {feed.provider}
                  </p>
                  {feed.errorCount > 0 && (
                    <p className="text-xs text-danger-600">
                      Error Count: {feed.errorCount}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-span-2 flex items-center">
                <p className="text-sm text-black dark:text-white truncate">
                  {feed.name}
                </p>
              </div>

              <div className="col-span-4 flex items-center">
                <a
                  href={feed.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-info hover:text-info-dark dark:text-info-light truncate block max-w-full"
                  title={feed.url}
                >
                  {feed.url}
                </a>
              </div>

              <div className="col-span-1 flex items-center">
                {getStatusBadge(feed)}
              </div>

              <div className="col-span-2 flex items-center">
                <div className="flex flex-wrap gap-1">
                  {feed.categories.slice(0, 3).map((category: string) => (
                    <span
                      key={category}
                      className="rounded-sm bg-neutral-100 px-2 py-1 text-xs text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300"
                    >
                      {category}
                    </span>
                  ))}
                  {feed.categories.length > 3 && (
                    <span className="text-xs text-neutral-600 dark:text-neutral-400">
                      +{feed.categories.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              <div className="col-span-1 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  tone="neutral"
                  size="xs"
                  className="rounded-sm"
                  onClick={() => setEditingFeed(feed)}
                >
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  tone="danger"
                  size="xs"
                  className="rounded-sm"
                  onClick={async () => {
                    await deleteRssFeed({ id: feed.id });
                    await refetch();
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="py-8 text-center">
            <p className="text-neutral-600 dark:text-neutral-400">
              No RSS feeds found. Run Bootstrap Feeds to seed baseline entries.
            </p>
          </div>
        )}

        {/* Footer Info */}
        {feeds && feeds.length > 0 && (
          <div className="border-t border-stroke py-3 px-6 dark:border-strokedark">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {feeds.length} feeds
            </p>
          </div>
        )}
      </SurfaceCard>

      <FeedEditorDialog
        open={isCreateOpen || editingFeed !== null}
        feed={editingFeed}
        onOpenChange={(open) => {
          if (!open) {
            setEditingFeed(null);
            setIsCreateOpen(false);
          }
        }}
        onSubmit={async (values) => {
          if (editingFeed) {
            await updateRssFeed({ id: editingFeed.id, ...values });
          } else {
            await createRssFeed(values);
          }
          setEditingFeed(null);
          setIsCreateOpen(false);
          await refetch();
        }}
      />
    </div>
  );
};

export default FeedsTable;
